/**
 * The Excel format codes TEXT(value, format_text) supports: a bounded subset,
 * each read here once so the planner and the engine agree on it.
 *
 * - `General`: the value as the Workbook shows it as text.
 * - Numbers: `0`, `#,##0`, either with `.` and 1 to 8 zeros, either with a
 *   trailing `%` (`0`, `0.00`, `#,##0`, `#,##0.00`, `0%`, `0.0%`). Rounded half
 *   away from zero to that many places.
 * - Dates: `yyyy`, `yy`, `mmmm`, `mmm`, `mm`, `m`, `dddd`, `ddd`, `dd` and `d`
 *   (any case), separated by `-`, `/`, `.`, `,` or spaces (`yyyy-mm-dd`,
 *   `mm/dd/yyyy`, `yyyymmdd`, `mmm yyyy`, `d-mmm-yy`). Names are English.
 *   `m` is always a month: there are no time codes.
 *
 * Any other code is a Diagnostic.
 */
export type TextFormat =
  | { kind: "general" }
  | { kind: "number"; decimals: number; grouping: boolean; percent: boolean }
  | { kind: "date"; parts: readonly DateFormatPart[] };

/** A date format's parts: a date code, or separator text shown as is. */
export type DateFormatPart =
  { kind: "code"; code: DateCode } | { kind: "text"; text: string };

export const DATE_CODES = [
  "yyyy",
  "yy",
  "mmmm",
  "mmm",
  "mm",
  "m",
  "dddd",
  "ddd",
  "dd",
  "d",
] as const;
export type DateCode = (typeof DATE_CODES)[number];

const NUMBER_FORMAT =
  /^(?<grouping>#,##)?0(?:\.(?<zeros>0{1,8}))?(?<percent>%)?$/;
const DATE_SEPARATORS = /^[-/., ]+/;
const MAX_FORMAT_LENGTH = 40;

function readDateFormat(code: string): TextFormat | null {
  const parts: DateFormatPart[] = [];
  let rest = code.toLowerCase();
  while (rest) {
    const separator = DATE_SEPARATORS.exec(rest)?.[0];
    if (separator) {
      parts.push({ kind: "text", text: separator });
      rest = rest.slice(separator.length);
      continue;
    }
    // Longest code first: DATE_CODES lists each letter's longest first.
    const dateCode = DATE_CODES.find((candidate) => rest.startsWith(candidate));
    if (!dateCode) return null;
    parts.push({ kind: "code", code: dateCode });
    rest = rest.slice(dateCode.length);
  }
  return parts.some((part) => part.kind === "code")
    ? { kind: "date", parts }
    : null;
}

/** A TEXT format code as the engine formats it, or null when it isn't supported. */
export function readTextFormat(code: string): TextFormat | null {
  if (!code || code.length > MAX_FORMAT_LENGTH) return null;
  if (code.toLowerCase() === "general") return { kind: "general" };
  const number = NUMBER_FORMAT.exec(code)?.groups;
  if (number)
    return {
      kind: "number",
      decimals: number.zeros?.length ?? 0,
      grouping: number.grouping !== undefined,
      percent: number.percent !== undefined,
    };
  return readDateFormat(code);
}
